import * as React from "react";
import styled from "@emotion/styled";
import { hexToRGB } from "styles/styleUtils";
import {
  Avatar,
  Button,
  FlexRow,
  H3,
  IconCheckRounded,
  IconReactionMehWhiteBg,
  IconReactionMustHugWhiteBg,
  IconReactionNahWhiteBg,
  IconReactionYayWhiteBg,
  IconReactionMustHug2,
  IconReactionChefsKiss,
  IconReactionMindBlowing,
  IconReactionNeedItNow,
  IconReactionObsessed,
  IconReactionOutOfThisWorld,
  IconReactionTakeMyMoney,
  IconReactionThoughtProvoking,
  IconX,
  Label,
  Link,
  ReviewTag,
  ScreenReaderText,
  Select,
  TagList,
  Text,
  Tooltip,
  EntityBadge,
} from "atoms";
import { ApiError } from "utils/api";
import { useAuth, useAuthHasRole } from "features/auth";
import {
  useModeratorFeedbackForm,
  useUpdateReviewVote,
} from "../api/useReviewVotes";
import type {
  GenericReview,
  ProjectReview,
  ArtistReview,
  ReviewVote,
} from "../types";
import type { ReviewSortOption } from "./ReviewSort";
import { editReviewPath } from "../utils/paths";

const projectReactionsMap: {
  [key in ProjectReview["rating"]]: React.ReactNode;
} = {
  "Must Hug": <IconReactionMustHugWhiteBg aria-label="hidden" />,
  Yay: <IconReactionYayWhiteBg aria-label="hidden" />,
  Meh: <IconReactionMehWhiteBg aria-label="hidden" />,
  Nah: <IconReactionNahWhiteBg aria-label="hidden" />,
};

const artistReactionsMap: {
  [key in ArtistReview["rating"]]: React.ReactNode;
} = {
  "Must Hug": <IconReactionMustHug2 aria-label="hidden" />,
  "Chef's kiss": <IconReactionChefsKiss aria-label="hidden" />,
  "Mind-blowing": <IconReactionMindBlowing aria-label="hidden" />,
  "Need it now": <IconReactionNeedItNow aria-label="hidden" />,
  Obsessed: <IconReactionObsessed aria-label="hidden" />,
  "Out of this world": <IconReactionOutOfThisWorld aria-label="hidden" />,
  "Take my money": <IconReactionTakeMyMoney aria-label="hidden" />,
  "Thought-provoking": <IconReactionThoughtProvoking aria-label="hidden" />,
};

const downvoteReasons = [
  "Low Effort",
  "Factually Incorrect",
  "Inappropriate",
  "Spam",
];

interface Props {
  review: ProjectReview | ArtistReview;
  usersVote?: ReviewVote;
  activeSort?: ReviewSortOption;
  showVotes?: boolean;
  showParent?: boolean;
  alwaysShowStatus?: boolean;
}

const getReviewerName = (
  displayName?: string,
  isAnonymous?: boolean
): string => {
  if (isAnonymous && displayName) {
    return `${displayName} [Anonymous]`;
  }
  if (isAnonymous || !displayName) {
    return "Anonymous";
  }
  return displayName || "Anonymous";
};

function Review({
  review,
  usersVote,
  activeSort,
  showVotes = true,
  showParent = false,
  alwaysShowStatus = false,
}: Props) {
  // Store ref to component root element for scroll logic
  const reviewElement = React.useRef<HTMLElement | null>(null);

  const { user } = useAuth();
  const isModerator = useAuthHasRole(["curator-level-2", "curator-level-3"]);
  const isHolder =
    !!user?.utilities?.find((u) => u === "Hugger") ||
    !!user?.guestPasses?.length;

  const {
    reviewId,
    createdAt,
    createdByUserId,
    displayName,
    displayAvatar,
    displayProjectName,
    rating,
    forYouIf,
    notForYouIf,
    artTags,
    communityTags,
    founderTeamTags,
    roadmapTags,
    status,
    counterUpvote,
    counterDownvote,
    myVote,
    isAnonymous,
  } = review;

  const isProject = review.reviewableType === "PROJECT";
  const isArtist = review.reviewableType === "ARTIST";

  let reactionsMap: { [key: string]: React.ReactNode } = {};
  if (isProject) reactionsMap = projectReactionsMap;
  if (isArtist) reactionsMap = artistReactionsMap;

  const isUsersReview = user?.id === createdByUserId;
  const requiresDownvoteReason =
    !isUsersReview && isModerator && review?.status === "PENDING";
  // A user can't vote on their own review or if they're not a HUG Pass holder
  const isNotVoter = isUsersReview || !isHolder;

  // NOTE: Reviews from Moderation page contain `myVote` while Reviews on PDP require separate call
  // to get all votes for given Project. Will Reviews on Profile match either of these two shapes?
  const userUpvoted = (myVote ?? usersVote?.vote) === "upvote";
  const userDownvoted = (myVote ?? usersVote?.vote) === "downvote";

  const showStatus = alwaysShowStatus || review?.status !== "PUBLISHED";

  const isAdmin = user?.roles.find((r) => r === "admin" || r === "superAdmin");

  const { register, handleSubmit } = useModeratorFeedbackForm();
  const [isShowingDownvoteForm, setIsShowingDownvoteForm] =
    React.useState(false);
  const toggleDownvoteForm = () =>
    setIsShowingDownvoteForm(!isShowingDownvoteForm);

  const { updateReviewVote, ...reviewVoteMutation } = useUpdateReviewVote({
    reviewId,
    reviewableType: review.reviewableType,
    reviewableId: review.reviewableId,
  });

  const scrollToReview = () => {
    if (activeSort === "upvotes") {
      // Make sure the user follows the review after any sorting change
      setTimeout(() => {
        reviewElement.current?.scrollIntoView({ block: "center" });
      }, 300);
    }
  };

  const handleVote = (vote: ReviewVote["vote"]) => () => {
    if (isNotVoter) return;

    updateReviewVote({ vote }, { onSuccess: scrollToReview });
  };

  const handleModeratorFeedback = handleSubmit((formData) => {
    if (isNotVoter) return;

    updateReviewVote(formData, {
      onSuccess: () => {
        scrollToReview();
        toggleDownvoteForm();
      },
    });
  });

  const reviewerName = getReviewerName(displayName, isAnonymous);

  const errorObject = reviewVoteMutation?.error
    ? (reviewVoteMutation.error as ApiError)
    : undefined;

  return (
    <>
      {showParent && (
        <ProjectTitleHeading>
          <Link
            to={`/${isProject ? "projects" : "artists"}/${review.reviewableId}`}
          >
            {displayProjectName}
          </Link>
          <EntityBadge>{review.reviewableType.toLowerCase()}</EntityBadge>
        </ProjectTitleHeading>
      )}

      <Article status={status} showStatus={showStatus} ref={reviewElement}>
        {showStatus && (
          <PendingText size="xs">
            <strong>
              {status[0]}
              {status.slice(1).toLowerCase()} review
            </strong>
          </PendingText>
        )}

        <Header>
          <ReviewAuthor>
            <ReviewAvatar
              src={isAnonymous ? undefined : displayAvatar}
              alt={`Profile picture for ${reviewerName}`}
            />
            <H3 size="sm">{reviewerName}</H3>
          </ReviewAuthor>
          <DateCreated size="xxs">
            Created{" "}
            {new Intl.DateTimeFormat([], { dateStyle: "medium" }).format(
              new Date(createdAt)
            )}
          </DateCreated>
          {isAdmin && (
            <span>
              <Link size="xs" to={editReviewPath(review)}>
                Edit Review
              </Link>
            </span>
          )}
        </Header>

        <Rating>
          {!!rating && (reactionsMap[rating] ?? null)}
          <ScreenReaderText>Reviewer rating:</ScreenReaderText>
          {rating || <em>No Rating</em>}
        </Rating>

        <div>
          <SText>
            <strong>
              This {isProject ? "project" : "artist"} is for you if:
            </strong>
            {forYouIf || <em>No Response</em>}
          </SText>

          {notForYouIf && (
            <SText>
              <strong>
                This {isProject ? "project" : "artist"} is NOT for you if:
              </strong>
              {notForYouIf}
            </SText>
          )}
        </div>

        <section>
          <TagList>
            <ScreenReaderText>
              Tags describing the project&rsquo;s founders:
            </ScreenReaderText>
            {founderTeamTags?.map((tag) => (
              <ReviewTag variant="founder" key={tag}>
                {tag}
              </ReviewTag>
            ))}

            <ScreenReaderText>
              Tags describing the project&rsquo;s community:
            </ScreenReaderText>
            {communityTags?.map((tag) => (
              <ReviewTag variant="community" key={tag}>
                {tag}
              </ReviewTag>
            ))}

            <ScreenReaderText>
              Tags describing the project&rsquo;s roadmap:
            </ScreenReaderText>
            {roadmapTags?.map((tag) => (
              <ReviewTag variant="roadmap" key={tag}>
                {tag}
              </ReviewTag>
            ))}

            <ScreenReaderText>
              Tags describing the project&rsquo;s art:
            </ScreenReaderText>
            {artTags?.map((tag) => (
              <ReviewTag variant="art" key={tag}>
                {tag}
              </ReviewTag>
            ))}
          </TagList>
        </section>

        {showVotes && (
          <footer>
            {isShowingDownvoteForm ? (
              <DownvoteForm onSubmit={handleModeratorFeedback}>
                <Label>
                  Select a reason:
                  <Select
                    {...register("moderatorFeedback", { required: true })}
                  >
                    <option value="">-</option>
                    {downvoteReasons.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </Select>
                </Label>
                <Button type="submit" variant="gradient">
                  Submit
                </Button>
                <Button onClick={toggleDownvoteForm}>Cancel</Button>
              </DownvoteForm>
            ) : (
              <>
                <Votes alignItems="center" justifyContent="flex-start">
                  <VoteText size="xs">
                    <strong>Is this review helpful?</strong>
                  </VoteText>
                  <Vote>
                    <Upvote
                      type="button"
                      onClick={handleVote("upvote")}
                      disabled={
                        isNotVoter ||
                        userUpvoted ||
                        reviewVoteMutation.isLoading
                      }
                      className={userUpvoted ? "current" : ""}
                    >
                      <IconCheckRounded />
                    </Upvote>
                    <span>{counterUpvote}</span>
                  </Vote>
                  <Vote>
                    <Downvote
                      type="button"
                      onClick={
                        requiresDownvoteReason
                          ? toggleDownvoteForm
                          : handleVote("downvote")
                      }
                      disabled={
                        isNotVoter ||
                        userDownvoted ||
                        reviewVoteMutation.isLoading
                      }
                      className={userDownvoted ? "current" : ""}
                    >
                      <IconX />
                    </Downvote>
                    <span>{counterDownvote}</span>
                  </Vote>
                </Votes>

                {!isHolder ? (
                  <STooltip label="Why can't I vote on reviews?">
                    <Text>
                      You must be holding a HUG Pass to interact with reviews.
                      If you already own a pass on a different wallet, sign out
                      and reconnect.
                    </Text>

                    <Text>
                      <Link to="/faq#hugpass">
                        Learn more about the HUG Pass.
                      </Link>
                    </Text>
                  </STooltip>
                ) : (
                  <STooltip
                    positionX="left"
                    positionY="top"
                    label="What makes a review helpful?"
                  >
                    <p>
                      <strong>Helpful reviews include the following:</strong>
                    </p>
                    <ul>
                      <li>They are clearly written</li>
                      <li>They provide useful information</li>
                      <li>Free from harmful and offensive language</li>
                      <li>Come from first-hand experience or knowledge</li>
                    </ul>
                    <p>
                      <Link to="/become-a-reviewer">
                        Learn more about our review standards.
                      </Link>
                    </p>
                  </STooltip>
                )}
              </>
            )}
          </footer>
        )}

        {reviewVoteMutation?.isError && (
          <pre>
            {JSON.stringify(
              {
                isError: reviewVoteMutation.isError,
                error: errorObject,
                message: errorObject?.message,
              },
              null,
              2
            )}
          </pre>
        )}
      </Article>
    </>
  );
}

type WithStatus = {
  status?: GenericReview["status"];
  showStatus: boolean;
};

const Article = styled.article<WithStatus>(({ theme, status, showStatus }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
  margin: "1rem 0 3rem",
  borderTop: `1px solid ${theme.colors.fg}`,
  paddingTop: showStatus ? 0 : "2rem",
  ".ReviewsColumn &": {
    ...(status === "PENDING" && {
      border: `3px solid ${theme.colors.pink}`,
      borderRadius: theme.borderRadius.md,
      overflow: "hidden",
      padding: "0 1rem 1rem 1rem",
    }),
  },
  ".ReviewSummary &": {
    "&>p": {
      display: "none !important",
    },
  },
  ".Moderation &": {
    border: `1px solid ${theme.colors.pink}`,
    borderRadius: theme.borderRadius.md,
    overflow: "hidden",
    paddingBottom: "1rem",
    paddingTop: "0",
    "&>*": {
      paddingLeft: "1rem",
      paddingRight: "1rem",
    },
  },
}));

const PendingText = styled(Text)(({ theme }) => ({
  backgroundColor: theme.colors.pink,
  padding: ".4em 1rem .3em 1rem",
  margin: "0",
}));

const Header = styled.header(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: ".5rem",
}));
const ReviewAuthor = styled.div(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: "1rem",
  h3: {
    ...theme.fonts.body,
    fontWeight: "bold",
    margin: "0 auto 0 0",
  },
}));

const ReviewAvatar = styled(Avatar)({
  width: "3rem",
  height: "3rem",
});

const SText = styled(Text)(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  marginBottom: 0,
  strong: {
    display: "block",
    marginBottom: "0.15rem",
    fontSize: theme.fontSizes.xxs,
  },
  "&+&": {
    marginTop: "1em",
  },
}));

const Rating = styled.div(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  fontSize: theme.fontSizes.xs,
  svg: {
    borderRadius: theme.borderRadius.round,
    height: "3rem",
    margin: "-.6em 0 -.6em -0.8em",
    width: "3rem",
  },
}));

const Votes = styled(FlexRow)(({ theme }) => ({
  display: "inline-flex",
  flex: "0 1 max-content !important",
  gap: "1rem",
  justifyContent: "flex-start",
  marginTop: ".5rem",
  scrollMargin: "10vh",
}));
const VoteText = styled(Text)({
  margin: 0,
});
const Vote = styled.div(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  flex: "0 1 max-content !important",
  fontSize: theme.fontSizes.xs,
  marginRight: ".5em",
  position: "relative",
  span: {
    alignItems: "center",
    backgroundColor: "white",
    border: `1px solid ${theme.colors.gray60}`,
    borderRadius: theme.borderRadius.round,
    display: "inline-flex",
    fontSize: "13px",
    height: "1.5em",
    justifyContent: "center",
    lineHeight: "1",
    minWidth: "1.5em",
    padding: "0 3px",
    position: "absolute",
    left: "100%",
    top: "0",
    transform: "translate(-30%, 0)",
    pointerEvents: "none",
  },
}));
const DateCreated = styled(Text)(({ theme }) => ({
  color: theme.colors.gray60,
  margin: 0,
}));
const VoteButton = styled.button(({ theme }) => ({
  alignItems: "center",
  appearance: "none",
  backgroundColor: "transparent",
  border: "0",
  borderRadius: theme.borderRadius.round,
  cursor: "pointer",
  display: "inline-flex",
  fontSize: theme.fontSizes.xs,
  height: "36px",
  justifyContent: "center",
  width: "36px",
  svg: {
    height: "12px",
    width: "auto",
  },
  "&.current": {
    border: "1px solid currentColor",
  },
  "&.current+span": {
    backgroundColor: theme.colors.black,
    borderColor: theme.colors.black,
    color: "white",
  },
  "&[disabled]": {
    cursor: "auto",
    opacity: ".5",
    color: theme.colors.black,
  },
  "&:not([disabled])": {},
  "&:not([disabled]):hover": {},
}));
const Upvote = styled(VoteButton)(({ theme }) => ({
  backgroundColor: theme.colors.green,
}));
const Downvote = styled(VoteButton)(({ theme }) => ({
  backgroundColor: hexToRGB(theme.colors.primary, 0.33),
}));

const STooltip = styled(Tooltip)(({ theme }) => ({
  "&>button": {
    color: theme.colors.gray60,
    marginTop: "1em",
    textDecoration: "underline",
  },
}));

const DownvoteForm = styled.form({
  label: {
    marginBottom: "1rem",
    maxWidth: 400,
  },
  button: {
    marginRight: "1rem",
  },
});

const ProjectTitleHeading = styled.h2(({ theme }) => ({
  ...theme.fonts.body,
  borderTop: `1px solid ${theme.colors.gray20}`,
  paddingTop: "1rem",
  marginTop: "2rem",
  fontWeight: "bold",
  fontSize: theme.fontSizes.md,
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
  alignItems: "center",
}));

export { Review };
